import React, { useCallback, useEffect, useRef, useState } from 'react';
import cc from 'classnames';
import { AgGridReact } from 'ag-grid-react';
import { ColDef, ICellRendererParams } from 'ag-grid-community';
import { useHistory } from 'react-router-dom';
import { FORM_ERROR } from 'final-form';
import { AxiosStatus } from 'api/axiosConfig';
import ErrorPage from 'ui/ErrorPage/ErrorPage';
import LoaderPage from 'ui/LoaderPage/LoaderPage';
import NoData from 'ui/structure/NoData/NoData';
import LoaderForTable from 'ui/LoaderForTable/LoaderForTable';
import TablePagination from 'ui/structure/TablePagination/TablePagination';
import DateEvent from 'ui/TableInputs/DateEvent/DateEvent';
import ApproveSection from 'ui/TableButtons/Approve_RejectButton/ApproveReject';
import { StatusModal, OTPVerificationModal } from 'components/modals';
import api from 'api/api';
import { FormType as FormTypeOTP } from 'components/modals/OTPVerificationModal/OTPVerificationModal';
import SourceAccountClick from 'ui/TableInputs/SourceAccountEvent/SourceAccountEvent';
import TransactionsRejectionModal from 'components/modals/TransactionRejectionModal/TransactionRejectionModal';
import { IOTPError } from 'components/authModules/LogIn/login.model';
import routePaths from 'routes/routePaths';
import { NETWORK_REQUIRED } from 'utils/constants';
import { IApiResponse } from 'utils/models';
import LinkToDetail from 'ui/TableInputs/LinkToDetail/LinkToDetail';
import { formattedDate, amountFormatter } from 'utils/formatters';
import {
  IPendingMobileMoney,
  IApiPendingMobileMoney,
  IPendingRejection,
  IRejectMMBody,
  ISubmitPendingMMBody,
  IPendingBulkMobileMoney,
} from '../../PendingMM.model';
import PendingBulkMMDetails from './PendingBulkMMDetails';
import noDataImg from 'assets/img/no_data_pending.png';

enum States {
  mainTable = 'mainTable',
  subTable = 'subTable',
}
const PendingBulkMobileMoney = () => {
  // Loading states
  const [state, setState] = useState<States>(States.mainTable);
  const [loadingStatus, setLoadingStatus] = useState<AxiosStatus>(AxiosStatus.DEFAULT);
  const pendingLoading = loadingStatus === AxiosStatus.PENDING;
  const successLoaded = loadingStatus === AxiosStatus.SUCCESS;
  const failureLoaded = loadingStatus === AxiosStatus.FAILURE;
  const [loading, setLoading] = useState(false);
  const [showNoDataWindow, setShowNoDataWindow] = useState(false);
  const [subTableTransactions, setsubTableTransactions] = useState<IPendingBulkMobileMoney>();
  const [isOpenSubData, setOpenSubData] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<AxiosStatus>(AxiosStatus.DEFAULT);
  const [OTPModalError, setOTPModalError] = useState<IOTPError | null>(null);
  const [isOTPModalOpen, setOTPModalOpen] = useState(false);
  const [isRejectionModalOpen, SetRejectionModalOpen] = useState(false);
  const gridRef = useRef<AgGridReact<IPendingMobileMoney>>(null);
  const [pageNumber, setPageNumber] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalRows, setTotalRows] = useState(0);

  const [successMsg, setSuccessMsg] = useState<string>('');
  const [errorMsg, setErrorMsg] = useState<string>('');

  const [rowData, setRowData] = useState<IPendingMobileMoney[]>([]);

  const history = useHistory();
  const handleOnSuccess = () => {
    history.push(routePaths.pendingRequests);
  };

  // Requesting Data for Table
  const requestDataForTable = () => {
    gridRef.current?.api?.showLoadingOverlay();
    const requestBody = {
      requestType: 'MOBILEMONEY-TRANSFER',
      pageSize,
      pageNumber,
    };
    api
      .post('transfer/pendingBulkTransactions', requestBody)
      .then((data: IApiPendingMobileMoney) => {
        setRowData(data.transactions);
        setTotalRows(data.size);
        if (data.size === 0) {
          setShowNoDataWindow(true);
        } else {
          setShowNoDataWindow(false);
        }
      })
      .catch((error: IApiResponse) => {
        console.error(error);
        setLoadingStatus(AxiosStatus.FAILURE);
        gridRef.current?.api.hideOverlay();
      });
  };
  useEffect(requestDataForTable, [pageNumber, pageSize]);

  // TODO fix double request for first render to display NoData component
  useEffect(() => {
    setLoadingStatus(AxiosStatus.PENDING);
    const requestBody = {
      requestType: 'MOBILEMONEY-TRANSFER',
      pageSize,
      pageNumber,
    };
    api
      .post('transfer/pendingBulkTransactions', requestBody)
      .then((data: IApiPendingMobileMoney) => {
        setLoadingStatus(AxiosStatus.SUCCESS);
        if (data.size === 0) {
          setShowNoDataWindow(true);
        } else {
          setShowNoDataWindow(false);
        }
      })
      .catch((error: IApiResponse) => {
        setLoadingStatus(AxiosStatus.FAILURE);
        console.error(error);
      });
  }, [pageNumber, pageSize]);

  // State for ApproveModal
  const openSubTable = () => {
    setOpenSubData(true);
    setState(States.subTable);
  };

  const closeOTPModal = () => {
    setOTPModalOpen(false);
    setSubmitStatus(AxiosStatus.DEFAULT);
  };
  const closeRejectionModal = () => {
    SetRejectionModalOpen(false);
    setSubmitStatus(AxiosStatus.DEFAULT);
  };
  const showStatusModal = submitStatus === AxiosStatus.SUCCESS || submitStatus === AxiosStatus.FAILURE;

  const onCloseStatusModal = () => {
    if (submitStatus === AxiosStatus.SUCCESS) {
      handleOnSuccess();
    }
    setSubmitStatus(AxiosStatus.DEFAULT);
  };
  const onRejectedSubmit = useCallback((error: IApiResponse) => {
    console.error(error || NETWORK_REQUIRED);
    setErrorMsg(error.description || NETWORK_REQUIRED);
    setSubmitStatus(AxiosStatus.FAILURE);
    setOTPModalOpen(false);
  }, []);

  const handleClickNameLink = (data: IPendingBulkMobileMoney) => () => {
    setsubTableTransactions(data);
    setOpenSubData(true);
    openSubTable();
    return setState(States.subTable);
  };
  //  Handle Submit approval
  const handleSubmitApprove = (data: IPendingBulkMobileMoney, otp: string) => {
    const body: ISubmitPendingMMBody = {
      transactionId: data.bulkId,
      password: otp,
      passwordType: 'OTP',
      requestType: 'BULK',
      transType: 'MOBILEMONEY-TRANSFER',
      approvalStatus: 'APPROVE',
    };
    setLoading(true);
    return api.post('transfer/approval', body);
  };

  // Handle Rejection with Reasons
  const handleSubmitReject = (data: IPendingBulkMobileMoney, reason: IPendingRejection) => {
    setSubmitStatus(AxiosStatus.PENDING);
    const body: IRejectMMBody = {
      transactionId: data.bulkId,
      reason: reason.rejectionReason,
      transType: 'MOBILEMONEY-TRANSFER',
      requestType: 'BULK',
      approvalStatus: 'REJECT',
    };
    api
      .post('transfer/rejection', body)
      .then((response: IApiPendingMobileMoney) => {
        closeRejectionModal();
        setSubmitStatus(AxiosStatus.SUCCESS);
        setSuccessMsg(response.description);
        requestDataForTable();
      })
      .catch((error: IApiResponse) => {
        setErrorMsg(error.description || NETWORK_REQUIRED);
        setSubmitStatus(AxiosStatus.FAILURE);
      })
      .finally(() => {
        gridRef.current?.api.hideOverlay();
      });
  };

  // Handle Approval with OTP SUBMIT
  const onOtpSubmit = useCallback(
    (value: IPendingBulkMobileMoney) => (otp: string | undefined, form: FormTypeOTP | undefined) => {
      setSubmitStatus(AxiosStatus.PENDING);
      api
        .post(`customer/otp/verify/${otp || ''}/false/${String(sessionStorage.getItem('userName'))}`)
        .then(() => {
          handleSubmitApprove(value, String(otp))
            .then((response: IApiResponse) => {
              setLoading(false);
              closeOTPModal();
              setSuccessMsg(response.description);
              setSubmitStatus(AxiosStatus.SUCCESS);
              requestDataForTable();
            })
            .catch((err: IApiResponse) => {
              onRejectedSubmit(err);
            });
        })
        .catch((error: IOTPError) => {
          setOTPModalError(error);
          if (error.failureCount === 0 && form) {
            form.initialize({ digit1: '', digit2: '', digit3: '', digit4: '', digit5: '' });
          }
          return { [FORM_ERROR]: error.description || NETWORK_REQUIRED };
        });
    },
    [],
  );
  const onResend = () => api.post('/customer/otp/generate');

  const handleCloseTxnModal = () => {
    setState(States.mainTable);
    setsubTableTransactions(undefined);
    setOpenSubData(false);
  };

  const handleApprove = (datas: IPendingBulkMobileMoney) => () => {
    setOTPModalOpen(true);
    setsubTableTransactions(datas);
    return api.post('/customer/otp/generate');
  };

  const handleReject = (datas: IPendingBulkMobileMoney | undefined): void => {
    SetRejectionModalOpen(true);
    setsubTableTransactions(datas);
  };

  // Columns & render
  const renderActions = (prop: ICellRendererParams<IPendingBulkMobileMoney, number | string>) => {
    if (prop.data) {
      return <ApproveSection onClickApprove={handleApprove(prop.data)} onClickReject={() => handleReject(prop.data)} />;
    }
    return false;
  };

  const renderNameLink = (prop: ICellRendererParams<IPendingBulkMobileMoney, number | string>) => {
    if (prop.data) {
      return <LinkToDetail content={String(prop.data?.bulkId)} onClick={handleClickNameLink(prop.data)} />;
    }
    return false;
  };

  const renderCreated = (prop: ICellRendererParams<IPendingMobileMoney>) => {
    if (prop.data) {
      const formatDate = formattedDate(prop.data?.createDate);
      return <DateEvent name={prop.data?.genericTransType} date={formatDate} />;
    }
    return null;
  };

  const renderAccount = (prop: ICellRendererParams<IPendingMobileMoney>) => {
    if (prop.data) {
      return <SourceAccountClick acctNumber={prop.data?.sourceAccount} acctType={prop.data?.sourceAccountType} />;
    }
    return null;
  };

  const renderTotalAmount = (prop: ICellRendererParams<IPendingMobileMoney>) => {
    if (prop.data && prop.data?.totalAmount) {
      return <div>{amountFormatter('ghs', false)(String(prop.data?.totalAmount))}</div>;
    }
    return null;
  };

  const renderELevy = (prop: ICellRendererParams<IPendingMobileMoney>) => {
    if (prop.data) {
      return <div>{prop.data?.elevyFee}</div>;
    }
    return null;
  };

  const renderTotalBeneficiries = (prop: ICellRendererParams<IPendingMobileMoney>) => {
    if (prop.data) {
      return <div>{prop.data?.totalCount}</div>;
    }
    return null;
  };

  const columnDefs: ColDef[] = [
    { field: 'bulkID', headerName: 'Bulk ID', flex: 1, minWidth: 225, cellRenderer: renderNameLink },
    { field: 'scheduleID', headerName: 'Schedule ID', flex: 1, minWidth: 200 },
    { field: 'sourceAccount', headerName: 'Source Account', flex: 1, minWidth: 150, cellRenderer: renderAccount },
    {
      field: 'totalBen',
      headerName: 'Total Beneficiaries',
      minWidth: 50,
      cellRenderer: renderTotalBeneficiries,
    },
    { field: 'totalAmount', headerName: 'Total Amount', flex: 1, minWidth: 130, cellRenderer: renderTotalAmount },
    { field: 'elevy', headerName: 'E-Levy', flex: 1, minWidth: 100, cellRenderer: renderELevy },
    { field: 'created', headerName: 'Created', flex: 1, minWidth: 200, cellRenderer: renderCreated },
    { field: 'action', headerName: '', width: 200, cellRenderer: renderActions },
  ];

  // Pagination & Filters handlers
  const handlePageChange = (event: React.ChangeEvent<unknown>, newPage: number) => {
    setPageNumber(newPage);
  };

  const handlePageSizeChange = (event: React.ChangeEvent<{ value: unknown }>) => {
    const newPageSize = parseInt(event.target.value as string, 10);
    setPageSize(newPageSize);
    setPageNumber(1);
  };

  if (state === States.subTable && subTableTransactions) {
    return (
      <PendingBulkMMDetails isOpen={isOpenSubData} onCloseTable={handleCloseTxnModal} data={subTableTransactions} />
    );
  }

  return (
    <>
      {pendingLoading && <LoaderPage />}
      {failureLoaded && <ErrorPage />}
      {successLoaded && (
        <>
          {/* <pre>{JSON.stringify(rowData, null, 2)}</pre> */}
          {showNoDataWindow ? (
            <NoData
              img={noDataImg}
              title="No pending requests yet"
              subtitle="Once you receive a request for approval, it appears here"
            />
          ) : (
            <>
              <div className={cc('tableWrapper', 'ag-theme-material')}>
                <AgGridReact
                  ref={gridRef}
                  rowData={rowData}
                  loadingOverlayComponent={LoaderForTable}
                  columnDefs={columnDefs}
                  defaultColDef={{ resizable: true }}
                  suppressCellFocus
                  enableCellTextSelection
                  headerHeight={60}
                  rowHeight={70}
                  undoRedoCellEditing
                  suppressRowHoverHighlight
                  pagination
                  paginationPageSize={pageSize}
                  suppressPaginationPanel
                  suppressScrollOnNewData
                  domLayout="autoHeight"
                />
              </div>
              <TablePagination
                count={Math.ceil(totalRows / pageSize)}
                page={pageNumber}
                pageSize={pageSize}
                totalCount={totalRows}
                handlePageChange={handlePageChange}
                handlePageSizeChange={handlePageSizeChange}
              />
            </>
          )}
          {isOTPModalOpen && subTableTransactions && (
            <OTPVerificationModal
              open={isOTPModalOpen}
              onClose={closeOTPModal}
              onSubmit={onOtpSubmit(subTableTransactions)}
              onResend={onResend}
              OTPModalError={OTPModalError}
              loading={loading}
            />
          )}
          {isRejectionModalOpen && subTableTransactions && (
            <TransactionsRejectionModal
              open={isRejectionModalOpen}
              onClose={closeRejectionModal}
              onReject={(reason) => {
                handleSubmitReject(subTableTransactions, reason);
              }}
            />
          )}
          <StatusModal
            open={showStatusModal}
            status={submitStatus === AxiosStatus.SUCCESS ? 'success' : 'failure'}
            onClose={onCloseStatusModal}
            onRetry={onCloseStatusModal}
            onRetryText="Close"
          >
            {submitStatus === AxiosStatus.SUCCESS ? successMsg : errorMsg}
          </StatusModal>
        </>
      )}
    </>
  );
};

export default PendingBulkMobileMoney;
